/*
 *  Shared modal shell.
 *
 *  Extracted from the per-page `ModalShell` copies (Vault, Finance, Schedule,
 *  Profile each grew their own). Adds a `size` prop, since a form with an
 *  embedded picker needs more width than the original hardcoded `max-w-md`,
 *  and Escape-to-close, which none of the copies had.
 *
 *  Backdrop click and Escape both close. The panel stops propagation so a
 *  click inside it never reaches the backdrop handler.
 */
import { ReactNode, useEffect } from "react";

const SIZES = {
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
} as const;

export interface ModalProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  size?: keyof typeof SIZES;
  onClose: () => void;
  children: ReactNode;
  /** Pinned to the bottom, outside the scrolling body — so a long form's
   *  submit button stays reachable without scrolling to it. */
  footer?: ReactNode;
}

export function Modal({
  title,
  subtitle,
  icon,
  size = "md",
  onClose,
  children,
  footer,
}: ModalProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65"
      onClick={onClose}
    >
      <div
        className={`card w-full ${SIZES[size]} overflow-hidden max-h-[90vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-white/5">
          {icon}
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-slate-100 leading-tight truncate">
              {title}
            </div>
            {subtitle && (
              <div className="text-[12px] text-slate-500 mt-0.5">{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-200 text-xl leading-none px-1 -mt-0.5"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
        {footer && (
          <div className="px-5 py-3.5 border-t border-white/5 flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
